import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { BEGRIFF, datumDe } from '../../components/marketingForm';
import { fetchData } from '../../helpers/apiUtils';
import type { FunnelPageType } from './funnel/api';
import LpFunnelPageDialog from './funnel/LpFunnelPageDialog';
import { ladeHinweise, type ProduktMedienHinweis } from './funnel/produktMedien/api';
import LpPageEditorDialog, { type LpPageType } from './LpPageEditorDialog';
import TypographyDefaultsTab from './TypographyDefaultsTab';

/**
 * Webinar-LPs (lp.finanztrends.de): per-webinar DOI + Aufzeichnung pages with fixed form
 * fields + server-rendered preview, plus the domain-wide legal texts. Backend:
 * routes/webinar-lp.ts / LpPageService. Docs: .claude/docs/lp-pages-system.md.
 */

interface PageStatus {
  status: 'draft' | 'published';
  published_at: string | null;
  updated_at: string | null;
}

interface WebinarRow {
  webinar_id: string;
  title: string;
  /** Produkt-Kuerzel (FUM, TSP …) und Name aus plx_products — Anzeige neben der Kennung. */
  plx_product?: string | null;
  plx_product_name?: string | null;
  start_date: string | null;
  next_occurrence_date: string | null;
  doi_url: string | null;
  missed_webinar_url: string | null;
  signup_url: string | null;
  order_url: string | null;
  pages: {
    doi?: PageStatus;
    recording?: PageStatus;
    signup?: PageStatus;
    order?: PageStatus;
    upsell?: PageStatus;
    thanks?: PageStatus;
  };
  doi_live: boolean;
  recording_live: boolean;
  signup_live: boolean;
  /**
   * Bestellseite: „live" = publiziert UND ein Verweis (order_url, Raum) zeigt auf sie. Die Regel
   * gehoert dem API (LpPageService.listWebinarsWithPages) — hier nie nachrechnen.
   */
  order_live: boolean;
}

/** Seitentypen der Übersicht — die Bestellstrecke (Bestellung, Upsell, Danke) hat einen eigenen Dialog (funnel/LpFunnelPageDialog). */
type LpPageKind = LpPageType | FunnelPageType;

interface LegalRow {
  doc_key: string;
  html: string;
  updated_by: string | null;
  updated_at: string;
}

/**
 * Drei Zustände in denselben Worten wie im Seiten-Editor und in der Bestellstrecke (Plan
 * 2026-10-22 Glossar): Entwurf = gespeichert, nicht im Netz · Veröffentlicht = im Netz, aber noch
 * nicht die aktive Seite des Webinars · Live = im Netz UND Ziel der Werbung/Links.
 */
function statusInfo(
  page: PageStatus | undefined,
  live: boolean,
): { label: string; farbe: string; bg: string } {
  if (live) return { label: BEGRIFF.live, farbe: '#1e6b3a', bg: '#e8f5ec' };
  if (!page) return { label: '+ anlegen', farbe: '#4b5563', bg: '#ffffff' };
  if (page.status === 'published')
    return { label: BEGRIFF.veroeffentlicht, farbe: '#1d4f91', bg: '#e8f1fd' };
  return { label: BEGRIFF.entwurf, farbe: '#8a4b00', bg: '#fff7ea' };
}

/** Heute in Berlin als 'YYYY-MM-DD' minus `tage` — Vergleich als Text gegen die Berliner Datumsspalten. */
function berlinTagMinus(tage: number): string {
  const d = new Date(Date.now() - tage * 86400000);
  const teile: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d))
    teile[p.type] = p.value;
  return `${teile.year}-${teile.month}-${teile.day}`;
}

/** Deep-Link `?seite=` (Webinar-Fahrplan) → Dialog. */
const DEEP_LINK_SEITEN = ['signup', 'doi', 'recording', 'bestellstrecke'] as const;
type DeepLinkSeite = (typeof DEEP_LINK_SEITEN)[number];

const PAGE_SLUGS: Record<LpPageKind, string> = {
  signup: 'anmeldung',
  doi: 'bestaetigung',
  recording: 'aufzeichnung',
  order: 'bestellen',
  upsell: 'angebot',
  thanks: 'danke',
};

/** Spalten + Knöpfe der PL-X-Strecke. Upsell/Danke kennen kein LIVE: die URL steht im PL-X-Upsell-Prozess. */
const FUNNEL_PAGES: Array<{ type: FunnelPageType; label: string }> = [
  { type: 'order', label: 'Bestellseite' },
  { type: 'upsell', label: 'Upsell' },
  { type: 'thanks', label: 'Danke' },
];

export function lpPageUrl(webinarId: string, pageType: LpPageKind): string {
  return `https://lp.finanztrends.de/w/${webinarId}/${PAGE_SLUGS[pageType]}/`;
}

/** Gespeicherter Stand als echte Seite — relative URL läuft durch CRA-Proxy (dev) und nginx (prod). */
export function lpPreviewUrl(webinarId: string, pageType: LpPageKind): string {
  return `/api/webinar-lp/pages/${webinarId}/${pageType}/preview.html`;
}

/**
 * Eine Seite in der Liste: der Status IST der Knopf (Klick öffnet die Seite zum Bearbeiten, bei
 * „+ anlegen“ ebenso), darunter klein Vorschau und Seite im Netz. Persona-Runde 2: die „—“-Kreise
 * wirkten nicht klickbar, und die getrennte Aktionen-Spalte war bei 1200 px abgeschnitten.
 */
function SeitenZelle({
  page,
  live,
  webinarId,
  pageType,
  titel,
  onOeffnen,
}: {
  page: PageStatus | undefined;
  live: boolean;
  webinarId: string;
  pageType: LpPageKind;
  titel: string;
  onOeffnen: () => void;
}) {
  const st = statusInfo(page, live);
  const published = live || page?.status === 'published';
  return (
    <Box display="flex" alignItems="center" gap={0.25} flexWrap="wrap">
      <Button
        size="small"
        variant="outlined"
        onClick={onOeffnen}
        title={`${titel} ${page ? 'öffnen und bearbeiten' : 'anlegen'}`}
        sx={{
          textTransform: 'none',
          minWidth: 0,
          px: 1,
          py: 0.1,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.6,
          color: st.farbe,
          bgcolor: st.bg,
          borderColor: page ? st.farbe : '#c4c9d0',
          borderStyle: page ? 'solid' : 'dashed',
          '&:hover': { bgcolor: st.bg, borderColor: st.farbe },
        }}
      >
        {st.label}
      </Button>
      {page && (
        <IconButton
          size="small"
          component="a"
          href={lpPreviewUrl(webinarId, pageType)}
          target="_blank"
          rel="noopener noreferrer"
          title="Vorschau (gespeicherter Stand)"
          sx={{ p: 0.25 }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      {published && (
        <IconButton
          size="small"
          component="a"
          href={lpPageUrl(webinarId, pageType)}
          target="_blank"
          rel="noopener noreferrer"
          title="Seite im Netz öffnen"
          sx={{ p: 0.25 }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}

/** Spalten mit fester Breite, damit bei 1200 px Fensterbreite alles ohne Querscrollen passt. */
const SPALTEN: Array<{ label: string; breite: number | string }> = [
  { label: 'Webinar', breite: 'auto' },
  { label: 'Termin', breite: 88 },
  { label: 'Anmeldung', breite: 118 },
  { label: 'Bestätigung', breite: 118 },
  { label: 'Aufzeichnung', breite: 118 },
  { label: 'Bestellstrecke', breite: 190 },
];

const LEGAL_DOCS: Array<{ key: string; label: string }> = [
  { key: 'impressum', label: 'Impressum' },
  { key: 'datenschutz', label: 'Datenschutzerklärung' },
];

const WebinarLp: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Die erlaubten Werte stehen als Konstante daneben: eine harte Abfrage wie
  // `=== 'legal' ? 'legal' : 'pages'` laesst einen neuen Reiter still auf der Seitenliste
  // landen, wenn jemand ihn per URL teilt (.claude/rules/react-frontend.md § Tabs).
  const TAB_VALUES = ['pages', 'legal', 'typography'] as const;
  const rawTab = searchParams.get('tab') || '';
  const tab = ((TAB_VALUES as readonly string[]).includes(rawTab)
    ? rawTab
    : 'pages') as (typeof TAB_VALUES)[number];

  const [rows, setRows] = useState<WebinarRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ webinarId: string; title: string; pageType: LpPageType } | null>(null);
  const [funnelDialog, setFunnelDialog] = useState<{
    webinarId: string;
    title: string;
    orderLive: boolean;
    pageType: FunnelPageType;
  } | null>(null);

  /** Standard: kommende Webinare + die letzten 14 Tage; „alle“ zeigt die ganze Liste. */
  const [zeitraum, setZeitraum] = useState<'kommend' | 'alle'>('kommend');
  const [suche, setSuche] = useState('');
  const sichtbareRows = useMemo(() => {
    const ab = berlinTagMinus(14);
    const q = suche.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.title.toLowerCase().includes(q) && !r.webinar_id.toLowerCase().includes(q))
        return false;
      if (zeitraum === 'alle' || q) return true;
      const tag = (r.next_occurrence_date || r.start_date || '').slice(0, 10);
      // Ohne Termin lieber zeigen als verstecken (sonst findet man ein frisch angelegtes nicht).
      return !tag || tag >= ab;
    });
  }, [rows, zeitraum, suche]);

  /**
   * Deep-Link aus dem Webinar-Fahrplan: `?webinar=<id>&seite=signup|doi|recording|bestellstrecke`
   * oeffnet nach dem Laden der Liste den passenden Dialog — unabhaengig vom Zeitraum-Filter (der
   * filtert nur die Anzeige). Beim Schliessen werden beide Parameter entfernt (replace).
   * `deepLinkHandled` verhindert das Wiederoeffnen nach `loadWebinars()` (Muster
   * scenes/webinar-management/index.tsx).
   */
  const deepLinkWebinarId = searchParams.get('webinar');
  const deepLinkSeiteRoh = searchParams.get('seite') || '';
  const deepLinkSeite: DeepLinkSeite | null = (DEEP_LINK_SEITEN as readonly string[]).includes(
    deepLinkSeiteRoh,
  )
    ? (deepLinkSeiteRoh as DeepLinkSeite)
    : null;
  const deepLinkHandled = useRef<string | null>(null);
  const clearDeepLink = () => {
    if (!searchParams.get('webinar') && !searchParams.get('seite')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('webinar');
        next.delete('seite');
        return next;
      },
      { replace: true },
    );
  };

  /** Produkt-Medien-Hinweise je Webinar (neues Cover / Bindung neu wählen) — nur Anzeige. */
  const [medienHinweise, setMedienHinweise] = useState<Record<string, ProduktMedienHinweis[]>>({});

  const [legal, setLegal] = useState<Record<string, string>>({});
  const [legalMeta, setLegalMeta] = useState<Record<string, LegalRow>>({});
  const [legalBusy, setLegalBusy] = useState<string | null>(null);
  const [legalMsg, setLegalMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const loadWebinars = useCallback(async () => {
    const res = await fetchData<WebinarRow[]>('/api/webinar-lp/webinars');
    if (res.success && res.data) {
      setRows(res.data);
      setError(null);
    } else {
      setError(res.error || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, []);

  const loadLegal = useCallback(async () => {
    const res = await fetchData<LegalRow[]>('/api/webinar-lp/legal');
    if (res.success && res.data) {
      const map: Record<string, string> = {};
      const meta: Record<string, LegalRow> = {};
      for (const row of res.data) {
        map[row.doc_key] = row.html;
        meta[row.doc_key] = row;
      }
      setLegal((prev) => ({ ...map, ...prev }));
      setLegalMeta(meta);
    }
  }, []);

  // Fehler (auch fehlende Migration) still ignorieren: die Übersicht darf daran nicht brechen.
  const loadMedienHinweise = useCallback(async () => {
    try {
      const res = await ladeHinweise();
      if (!res.success || !res.data) return;
      const map: Record<string, ProduktMedienHinweis[]> = {};
      for (const h of res.data.hinweise || []) {
        if (!map[h.webinarId]) map[h.webinarId] = [];
        map[h.webinarId].push(h);
      }
      setMedienHinweise(map);
    } catch {
      // bewusst still
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadWebinars();
      await loadLegal();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadWebinars, loadLegal]);

  useEffect(() => {
    void loadMedienHinweise();
  }, [loadMedienHinweise]);

  /** Bestellstrecken-Dialog öffnen — derselbe Weg wie der Knopf in der Zeile (auch „anlegen“). */
  const oeffneStrecke = useCallback((webinarId: string, row: WebinarRow | undefined) => {
    setFunnelDialog({
      webinarId,
      title: row?.title ?? webinarId,
      orderLive: row?.order_live ?? false,
      pageType: (row && FUNNEL_PAGES.find((f) => row.pages[f.type])?.type) ?? 'order',
    });
  }, []);

  useEffect(() => {
    const schluessel = deepLinkWebinarId ? `${deepLinkWebinarId}:${deepLinkSeite ?? ''}` : null;
    if (!schluessel || !deepLinkWebinarId) {
      // Parameter weg (Dialog geschlossen) → derselbe Link darf spaeter wieder oeffnen.
      deepLinkHandled.current = null;
      return;
    }
    if (loading || deepLinkHandled.current === schluessel) return;
    deepLinkHandled.current = schluessel;
    const row = rows.find((r) => r.webinar_id === deepLinkWebinarId);
    // Nicht in der Liste (inaktiv oder jenseits der 200 neuesten): trotzdem oeffnen — der Dialog
    // laedt seine Daten selbst; nur der Titel faellt auf die ID zurueck.
    if (deepLinkSeite === 'bestellstrecke') {
      oeffneStrecke(deepLinkWebinarId, row);
    } else {
      setEditor({
        webinarId: deepLinkWebinarId,
        title: row?.title ?? deepLinkWebinarId,
        pageType: deepLinkSeite ?? 'signup',
      });
    }
    // setSearchParams ist NICHT referenzstabil und gehoert nicht in die Deps
    // (reference-react-router-setsearchparams).
  }, [deepLinkWebinarId, deepLinkSeite, loading, rows, oeffneStrecke]);

  const saveLegal = async (docKey: string) => {
    setLegalBusy(docKey);
    setLegalMsg(null);
    const res = await fetchData<{ url: string; warnings: string[] }>(
      `/api/webinar-lp/legal/${docKey}`,
      { method: 'PUT', body: JSON.stringify({ html: legal[docKey] || '' }) },
    );
    setLegalBusy(null);
    if (!res.success) {
      setLegalMsg({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
      return;
    }
    const warnings = res.data?.warnings || [];
    setLegalMsg({
      severity: 'success',
      text: `Gespeichert und live: ${res.data?.url}${warnings.length ? ` — ${warnings.join(' · ')}` : ''}`,
    });
    loadLegal();
  };

  return (
    <Box m="20px">
      <Header
        title="Webinar LPs"
        subtitle="Alle Seiten eines Webinars auf lp.finanztrends.de: Anmeldung, Bestätigung, Aufzeichnung und Bestellstrecke (Bestellseite, Upsell, Danke)"
      />

      <Tabs
        value={tab}
        onChange={(_e, v) => setSearchParams({ tab: v })}
        sx={{ mb: 2 }}
      >
        <Tab value="pages" label="Webinar-Seiten" />
        <Tab value="legal" label="Rechtstexte" />
        <Tab value="typography" label="Typografie-Defaults" />
      </Tabs>

      {tab === 'pages' && (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={zeitraum}
              onChange={(_e, v) => v && setZeitraum(v)}
            >
              <ToggleButton value="kommend" sx={{ textTransform: 'none' }}>
                Kommende + letzte 14 Tage
              </ToggleButton>
              <ToggleButton value="alle" sx={{ textTransform: 'none' }}>
                Alle
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              label="Suche nach Titel oder ID"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            {!loading && (
              <Typography variant="caption" color="text.secondary">
                {sichtbareRows.length} von {rows.length} Webinaren
                {suche.trim() ? ' (Suche durchsucht alle Termine)' : ''}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            <b>Klick auf einen Status öffnet die Seite zum Bearbeiten</b> („+ anlegen“ legt sie an) ·{' '}
            <b>{BEGRIFF.entwurf}</b> = gespeichert, noch nicht im Netz ·{' '}
            <b>{BEGRIFF.veroeffentlicht}</b> = im Netz erreichbar, aber noch nicht die aktive Seite
            des Webinars · <b>{BEGRIFF.live}</b> = im Netz UND Ziel von Werbung und Links (Upsell und
            Danke werden nie „Live“ — sie verlinkt PL-X) · Auge = Vorschau, Pfeil = Seite im Netz ·{' '}
            <Box component="span" sx={{ bgcolor: '#ed6c02', color: '#fff', px: 0.5, borderRadius: 0.5 }}>
              Bilder prüfen
            </Box>{' '}
            = in den Produkt-Medien gibt es eine neue Fassung (z. B. neues Cover) — die Bestellstrecke
            öffnen, prüfen und veröffentlichen; nichts geht von selbst live.
          </Typography>
          {loading ? (
            <Box p={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : sichtbareRows.length === 0 ? (
            <Alert severity="info">
              Kein Webinar passt{suche.trim() ? ` zu „${suche.trim()}“` : ' in den Zeitraum'} —
              {zeitraum === 'kommend' ? ' oben „Alle“ wählen oder' : ''} die Suche ändern.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 640 }}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    {SPALTEN.map((h) => (
                      <TableCell
                        key={h.label}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                          width: h.breite,
                          px: 1,
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sichtbareRows.map((row) => {
                    const hatStrecke = FUNNEL_PAGES.some((f) => row.pages[f.type]);
                    const oeffne = (pageType: LpPageType) =>
                      setEditor({ webinarId: row.webinar_id, title: row.title, pageType });
                    return (
                      <TableRow key={row.webinar_id} hover>
                        <TableCell sx={{ color: 'black', px: 1, wordBreak: 'break-word' }}>
                          <Typography variant="body2" fontWeight={600} color="inherit">
                            {row.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.webinar_id}
                            {row.plx_product && (
                              <Tooltip title={row.plx_product_name || ''}>
                                <span> · {row.plx_product}</span>
                              </Tooltip>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: 'black', px: 1 }}>
                          {datumDe(row.next_occurrence_date || row.start_date) || '—'}
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <SeitenZelle
                            page={row.pages.signup}
                            live={row.signup_live}
                            webinarId={row.webinar_id}
                            pageType="signup"
                            titel="Anmeldeseite"
                            onOeffnen={() => oeffne('signup')}
                          />
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <SeitenZelle
                            page={row.pages.doi}
                            live={row.doi_live}
                            webinarId={row.webinar_id}
                            pageType="doi"
                            titel="Bestätigungsseite"
                            onOeffnen={() => oeffne('doi')}
                          />
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <SeitenZelle
                            page={row.pages.recording}
                            live={row.recording_live}
                            webinarId={row.webinar_id}
                            pageType="recording"
                            titel="Aufzeichnungsseite"
                            onOeffnen={() => oeffne('recording')}
                          />
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          {/* Fuer JEDES Webinar: ohne Funnel-Seite legt der Dialog sie per Upload an. */}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => oeffneStrecke(row.webinar_id, row)}
                            title={hatStrecke ? 'Bestellstrecke öffnen' : 'Bestellstrecke anlegen'}
                            sx={{
                              textTransform: 'none',
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              px: 1,
                              py: 0.25,
                              borderStyle: hatStrecke ? 'solid' : 'dashed',
                              borderColor: hatStrecke ? '#9aa5b1' : '#c4c9d0',
                              color: '#1f2937',
                              display: 'block',
                              width: '100%',
                            }}
                          >
                            {hatStrecke ? (
                              FUNNEL_PAGES.map((f) => {
                                const st = statusInfo(
                                  row.pages[f.type],
                                  f.type === 'order' && row.order_live,
                                );
                                return (
                                  <Box key={f.type} sx={{ fontSize: 11.5, lineHeight: 1.45 }}>
                                    {f.label}:{' '}
                                    <Box component="span" sx={{ color: st.farbe, fontWeight: 600 }}>
                                      {row.pages[f.type] ? st.label : 'fehlt'}
                                    </Box>
                                  </Box>
                                );
                              })
                            ) : (
                              <Box sx={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>
                                + Bestellstrecke anlegen
                              </Box>
                            )}
                          </Button>
                          {medienHinweise[row.webinar_id] && (
                            <Tooltip
                              title={medienHinweise[row.webinar_id].map((h) => h.text).join(' · ')}
                              arrow
                            >
                              <Chip
                                size="small"
                                label="Bilder prüfen"
                                data-testid={`medien-hinweis-${row.webinar_id}`}
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: '#ed6c02',
                                  color: '#ffffff',
                                  cursor: 'help',
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 'legal' && (
        <Box display="flex" flexDirection="column" gap={3} maxWidth={900}>
          <Alert severity="info">
            Domainweit für lp.finanztrends.de — jede LP verlinkt beide Dokumente im Footer.
            Speichern rendert die Seite sofort live (/impressum/, /datenschutz/). Bearbeitung ist
            Admins vorbehalten.
          </Alert>
          {legalMsg && <Alert severity={legalMsg.severity}>{legalMsg.text}</Alert>}
          {LEGAL_DOCS.map((doc) => (
            <Paper key={doc.key} sx={{ p: 2, backgroundColor: '#ffffff' }}>
              <Typography variant="h6" sx={{ color: 'black', mb: 1 }}>
                {doc.label}
              </Typography>
              {legalMeta[doc.key] && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Zuletzt geändert: {legalMeta[doc.key].updated_at?.slice(0, 16).replace('T', ' ')} von{' '}
                  {legalMeta[doc.key].updated_by || 'unbekannt'}
                </Typography>
              )}
              <TextField
                value={legal[doc.key] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setLegal((prev) => ({ ...prev, [doc.key]: value }));
                }}
                fullWidth
                multiline
                minRows={8}
                placeholder="HTML-Inhalt"
                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
              />
              <Button
                variant="contained"
                sx={{ mt: 1 }}
                disabled={legalBusy !== null}
                onClick={() => saveLegal(doc.key)}
              >
                {legalBusy === doc.key ? 'Speichert…' : 'Speichern + live stellen'}
              </Button>
            </Paper>
          ))}
        </Box>
      )}

      {tab === 'typography' && <TypographyDefaultsTab />}

      {/* orderLive aus der FRISCHEN Liste: nach „Live schalten" im Dialog laedt onChanged sie neu. */}
      {funnelDialog && (
        <LpFunnelPageDialog
          webinarId={funnelDialog.webinarId}
          webinarTitle={funnelDialog.title}
          orderLive={
            rows.find((r) => r.webinar_id === funnelDialog.webinarId)?.order_live ?? funnelDialog.orderLive
          }
          initialPageType={funnelDialog.pageType}
          onClose={() => {
            setFunnelDialog(null);
            clearDeepLink();
            // Neue Medien oder ein Publish können Hinweise erzeugt oder erledigt haben.
            void loadMedienHinweise();
          }}
          onChanged={loadWebinars}
        />
      )}
      {editor && (
        <LpPageEditorDialog
          open
          webinarId={editor.webinarId}
          webinarTitle={editor.title}
          pageType={editor.pageType}
          sources={rows}
          isLive={(() => {
            const r = rows.find((x) => x.webinar_id === editor.webinarId);
            if (!r) return undefined;
            return editor.pageType === 'signup'
              ? r.signup_live
              : editor.pageType === 'doi'
                ? r.doi_live
                : r.recording_live;
          })()}
          onClose={() => {
            setEditor(null);
            clearDeepLink();
          }}
          onChanged={loadWebinars}
        />
      )}
    </Box>
  );
};

export default WebinarLp;
